import Head from "next/head";
import { useRouter } from "next/router";

// Define an Author type
interface Author {
  name: string;
  url?: string; // Optional URL to author bio page
  sameAs?: string[]; // Optional array of social/profile links
}

interface HeaderProps {
  title?: string;
  description?: string;
  canonical?: string;
  structuredData?: Record<string, unknown>; // Use Record<string, unknown> for flexibility
  metaImage?: string; // Added metaImage prop

  // Article specific props
  ogType?: "website" | "article"; // Added ogType
  publishedTime?: string; // ISO 8601 format
  modifiedTime?: string; // ISO 8601 format
  author?: Author; // Changed from authorName to author object
  tags?: string[];
}

// Define default structured data (type is inferred)
const defaultStructuredData = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "Promptwatch",
  url: "https://promptwatch.com",
  alternateName: "Promptwatch.com",
  logo: "https://promptwatch.com/favicon.svg",
  description: "Promptwatch.com is the official Promptwatch, an AI search/GEO tracking tool.",
  sameAs: ["https://twitter.com/promptwatchcom", "https://x.com/promptwatchcom", "https://www.linkedin.com/company/promptwatch"],
  publisher: {
    "@type": "Organization",
    name: "Promptwatch",
  },
};

const Header = ({
  title = "Promptwatch.com | Get your company mentioned in AI search results",
  description = "Boost your company's visibility in AI search engines and optimize your presence in AI-generated content from ChatGPT, Perplexity, Claude, Gemini and other LLMs",
  canonical,
  structuredData, // Destructure the new prop
  metaImage, // Destructure the new prop
  ogType = "website", // Default to website
  publishedTime,
  modifiedTime,
  author, // Use the author object
  tags,
}: HeaderProps) => {
  const router = useRouter();
  const currentUrl = `https://promptwatch.com${router.asPath}`;
  const canonicalUrl = canonical || currentUrl;

  // Determine structured data based on ogType
  const finalStructuredData =
    structuredData || // Use custom if provided
    (ogType === "article"
      ? {
          "@context": "https://schema.org",
          "@type": "BlogPosting", // More specific than Article
          headline: title,
          description: description,
          image: metaImage || "https://promptwatch.com/meta.png", // Use metaImage if available, otherwise default
          author: author
            ? {
                "@type": "Person",
                name: author.name,
                ...(author.url && { url: author.url }),
                ...(author.sameAs && { sameAs: author.sameAs }),
              }
            : undefined,
          publisher: {
            "@type": "Organization",
            name: "Promptwatch.com",
            url: "https://promptwatch.com",
            logo: {
              "@type": "ImageObject",
              url: "https://promptwatch.com/favicon.svg", // Or specific logo
            },
          },
          url: canonicalUrl,
          datePublished: publishedTime,
          dateModified: modifiedTime || publishedTime, // Use published if modified not available
          mainEntityOfPage: {
            "@type": "WebPage",
            "@id": canonicalUrl,
          },
          // Add keywords if tags are available
          ...(tags && tags.length > 0 && { keywords: tags.join(", ") }),
        }
      : defaultStructuredData); // Fallback to default Organization data

  return (
    <Head>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="robots" content="index, follow" />
      <meta name="viewport" content="width=device-width, initial-scale=1, user-scalable=yes" />
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} /> {/* Use dynamic ogType */}
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={metaImage || "https://promptwatch.com/meta.png"} />
      <meta property="og:site_name" content="Promptwatch.com" />
      <meta property="og:locale" content="en_US" /> {/* Added locale */}
      {/* Article specific OG tags */}
      {ogType === "article" && publishedTime && <meta property="article:published_time" content={publishedTime} />}
      {ogType === "article" && modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}
      {ogType === "article" && author && <meta property="article:author" content={author.name} />}
      {/* TODO: Add article:section if available */}
      {ogType === "article" && tags && tags.map((tag) => <meta property="article:tag" content={tag} key={tag} />)}
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      {/* TODO: Replace with your Twitter handle */}
      <meta name="twitter:site" content="@promptwatchcom" />
      {/* TODO: Replace with the content creator's Twitter handle if different */}
      {/* Use author name for creator handle, fallback to site handle */}
      <meta name="twitter:creator" content={author?.name ? `@${author.name.replace(/\s+/g, "")}` : "@promptwatchcom"} />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={metaImage || "https://promptwatch.com/meta.png"} />
      {/* Article specific Twitter tags */}
      {ogType === "article" && author && <meta name="twitter:label1" content="Written by" />}
      {ogType === "article" && author && <meta name="twitter:data1" content={author.name} />}
      {/* TODO: Add label2/data2 for Category/Section if available */}
      {/* {ogType === 'article' && section && <meta name="twitter:label2" value="Filed under" />} */}
      {/* {ogType === 'article' && section && <meta name="twitter:data2" value={section} />} */}
      {/* Favicon */}
      <link rel="icon" type="image/png" href="/favicon-96x96.png" sizes="96x96" />
      <link rel="icon" type="image/svg+xml" href="/favicon.svg" />
      <link rel="shortcut icon" href="/favicon.ico" />
      {/* Apple specific */}
      <link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png" />
      <meta name="apple-mobile-web-app-title" content="Promptwatch.com" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      {/* Theme color */}
      <meta name="theme-color" content="#155EEF" />
      <meta name="apple-mobile-web-app-status-bar" content="#155EEF" />
      {/* PWA */}
      <link rel="manifest" href="/site.webmanifest" />
      {/* Canonical */}
      <link rel="canonical" href={canonicalUrl} />
      {/* Structured Data (JSON-LD) */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          // Use the determined structured data
          __html: JSON.stringify(finalStructuredData),
        }}
      />
    </Head>
  );
};

export { Header };
